import { getCurrencySymbol } from '@angular/common'
import { template as _template } from 'lodash'
import moment from 'moment'

import { Clinic } from '../model/clinic'
import { formatPhoneNumber } from './phone'

const calendarWithTimeFormats = {
  en: {
    // LT means local time format, which defaults to 'h:mm A'
    sameDay: '[today at] LT',
    nextDay: '[tomorrow at] LT',
    nextWeek: '[on] dddd [at] LT',
    lastDay: '[yesterday at] LT',
    lastWeek: '[last] dddd [at] LT',
    sameElse: '[on] L [at] LT',
  },

  es: {
    sameDay() {
      return '[hoy a la' + (this.hours() !== 1 ? 's' : '') + '] LT'
    },
    nextDay() {
      return '[mañana a la' + (this.hours() !== 1 ? 's' : '') + '] LT'
    },
    nextWeek() {
      return 'dddd [a la' + (this.hours() !== 1 ? 's' : '') + '] LT'
    },
    lastDay() {
      return '[ayer a la' + (this.hours() !== 1 ? 's' : '') + '] LT'
    },
    lastWeek() {
      return '[el] dddd [pasado a la' + (this.hours() !== 1 ? 's' : '') + '] LT'
    },
    sameElse: 'L LT',
  },
}

const calendarWithoutTimeFormats = {
  en: {
    // LT means local time format, which defaults to 'h:mm A'
    sameDay: '[today]',
    nextDay: '[tomorrow]',
    nextWeek: '[on] dddd',
    lastDay: '[yesterday]',
    lastWeek: '[last] dddd',
    sameElse: '[on] L',
  },

  es: {
    sameDay: '[hoy]',
    nextDay: '[mañana]',
    nextWeek: 'dddd',
    lastDay: '[ayer]',
    lastWeek: '[el] dddd [pasado]',
    sameElse: 'L',
  },
}

const linkTokens = [
  'affirmativeConfirmLink',
  'confirmLink',
  'facilityAddressLink',
  'intakeLink',
  'negativeConfirmLink',
  'rescheduleLink',
  'scheduleLink',
  'shortIntakeLink',
  'shortLink',
  'shortRescheduleLink',
  'shortScheduleLink',
  'shortConfirmLink',
  'shortWaitlistLink',
  'waitlistLink',
]

export function renderTemplatePreview(
  temp: string,
  format: 'html' | 'text',
  language: string,
  clinic: Clinic,
) {
  for (const token of linkTokens) {
    const plainToken = new RegExp(`{${token}}`, 'g')
    const complexToken = new RegExp(`{${token}:([^}]+)}`, 'g')

    if (format === 'html') {
      temp = temp.replace(
        plainToken,
        `<a href="<%= ${token} %>" class="link-box"><%= ${token} %></a>`,
      )
      temp = temp.replace(
        complexToken,
        `<div class="link-button-parent"><a href="<%= ${token} %>" class="link-button">$1</a></div>`,
      )
    } else {
      temp = temp.replace(plainToken, `<%= ${token} %>`)
      temp = temp.replace(complexToken, `<%= ${token} %>`)
    }
  }

  // support complex dates
  temp = temp.replace(
    new RegExp(`{date:([^}]+)}`, 'g'),
    `<%= date ? date.format('$1') : '' %>`,
  )

  // support subtracting time from dateTime
  temp = temp.replace(
    /{dateTime-([\d.]+)([^}]+)}/g,

    `<%= date && calendarWithTimeFormat ? date.clone().subtract($1, '$2'.trim().toLowerCase() || 'minutes').calendar(undefined, calendarWithTimeFormat) : '' %>`,
  )

  // replace all other tokens with template variable syntax
  temp = temp.replace(/{([a-zA-Z]*)}/g, '<%= $1 %>')

  const date = moment().local()
  const locality = language === 'es' ? 'es-us' : language

  try {
    const template = _template(temp)
    const currencySymbol = clinic.settings.defaultCurrency
      ? getCurrencySymbol(clinic.settings.defaultCurrency, 'wide')
      : getCurrencySymbol('USD', 'wide')

    return template({
      clinicName: clinic.name,
      clinicPhone: formatPhoneNumber(clinic.phoneNumber, '111-222-3333'),
      code: '123-456',
      date,
      language,
      calendarWithTimeFormats,
      calendarWithoutTimeFormats,
      calendarWithTimeFormat: calendarWithTimeFormats.en,
      calendarWithoutTimeFormat: calendarWithoutTimeFormats.en,
      dateTime: date
        .locale(locality)
        .calendar(undefined, calendarWithTimeFormats[language]),
      calendarDate: date
        .locale(locality)
        .calendar(undefined, calendarWithoutTimeFormats[language]),
      facilityAddress: '1 Main St, Downtown, MU 10101',
      locationAddress: '1 Main St, Downtown, MU 10101', // legacy
      facilityName: 'Quick Care Hospital',
      facilityPhone: '888-234-5678',
      affirmativeConfirmLink: 'https://ppal.io/afrmlink',
      confirmLink: 'https://ppal.io/conflink',
      negativeConfirmLink: 'https://ppal.io/~neglink',
      patientFamilyName: 'Smith',
      patientFullName: 'John Smith',
      patientGivenName: 'John',
      procedures: 'MRI LEFT KNEE, MRI RIGHT KNEE',
      procedureCodes: '12345, 12345',
      referrerName: 'Dr. Smith',
      remainingAmount: currencySymbol + '1500.00',
      servicePhone: formatPhoneNumber(clinic.twilioNumber, '111-222-3333'),
      shortLink: 'https://ppal.io/~smlink',
      shortTransactionLink: 'https://ppal.io/~tlink',
      totalAmount: currencySymbol + '500.00',
      facilityAddressLink: 'https://ppal.io/facilityAddressLink',
      shortRescheduleLink: 'https://ppal.io/shortScheduleLink',
      shortScheduleLink: 'https://ppal.io/shortScheduleLink',
      shortIntakeLink: 'https://ppal.io/shortIntakeLink',
      shortWaitlistLink: 'https://ppal.io/shortWaitlistLink',
    })
  } catch (ex) {
    return '[Preview not available, check formatting of template]'
  }
}
